import { useState, useRef, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../shared/lib/supabaseClient';
import { useAuth } from '../../auth/context/AuthContext';
import { ManualTransactionFormData } from '../components/modals/ManualTransactionForm';
import { toast } from 'react-hot-toast';
import { BASE_URL } from '../../../shared/lib/api';

/**
 * useTransactionManagement
 * 
 * Custom hook for managing transaction-related CRUD operations.
 * Handles transaction creation, editing, and deletion with proper
 * cache invalidation and error handling.
 */

// Helper functions for normalization
const normTicker = (t?: string | null) => {
  return (t ?? '').trim().toUpperCase() || null;
};

const normType = (t?: string | null) => {
  const v = (t ?? '').trim();
  return v ? v.toUpperCase() : null;
};

export interface TransactionSubmitResult {
  success: boolean;
  needsTickerFetch?: boolean;
  newTicker?: string | null;
}

export interface UseTransactionManagementReturn {
  // State
  editingTransaction: ManualTransactionFormData | undefined;

  // Actions
  handleManualTransactionSubmit: (
    data: ManualTransactionFormData,
    accountId: string
  ) => Promise<TransactionSubmitResult>;
  handleEditTransactionClick: (transaction: ManualTransactionFormData) => void;
  handleDeleteTransaction: (transactionId: string, accountId: string) => Promise<void>;
  clearEditingTransaction: () => void;
  setEditingTransaction: (transaction: ManualTransactionFormData | undefined) => void;
}

export const useTransactionManagement = (
  onTransactionChanged?: (accountId: string) => Promise<void>
): UseTransactionManagementReturn => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [editingTransaction, setEditingTransaction] = useState<
    ManualTransactionFormData | undefined
  >(undefined);

  // Snapshot of original ticker/type data used to compare with any changes made
  const originalRef = useRef<{
    ticker: string | null;
    type: string | null;
    txId: string | null;
  }>({
    ticker: null,
    type: null,
    txId: null,
  });

  // Capture snapshot when editing starts
  useEffect(() => {
    if (editingTransaction?.id) {
      const snapshot = {
        ticker: editingTransaction.ticker ?? null,
        type: editingTransaction.type ?? null,
        txId: editingTransaction.id,
      };
      originalRef.current = snapshot;
    } else {
      originalRef.current = { ticker: null, type: null, txId: null };
    }
  }, [editingTransaction?.id, editingTransaction?.ticker, editingTransaction?.type]);

  // Submit transaction (create or edit)
  const handleManualTransactionSubmit = useCallback(
    async (data: ManualTransactionFormData, accountId: string): Promise<TransactionSubmitResult> => {
      if (!accountId) {
        alert('Error: No account selected.');
        return { success: false };
      }

      const isEdit = !!data.id;

      // Snapshot captured when editor opened
      let oldTicker = normTicker(originalRef.current.ticker);
      let oldType = normType(originalRef.current.type);

      // If snapshot is somehow empty but this is an edit, fall back to the editingTransaction values
      if (isEdit && !oldTicker && !oldType && editingTransaction) {
        oldTicker = normTicker(editingTransaction.ticker ?? null);
        oldType = normType(editingTransaction.type ?? null);
      }

      // Include originals for every edit when available so recalculation can account for both sides.
      const payloadOriginals =
        isEdit && (oldTicker || oldType)
          ? { original_ticker: oldTicker, original_type: oldType }
          : undefined;

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('No auth token');

        const res = await fetch(`${BASE_URL}/transactions/manual/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            account_id: accountId,
            transaction: data,
            transaction_change_type: isEdit ? 'edit_transaction' : 'new_transaction',
            ...(payloadOriginals ?? {}),
          }),
        });

        const result = await res.json().catch(() => ({}));
        if (!res.ok) {
          // Check for TICKER_NOT_FOUND_OPTIONAL error code
          if (result?.error_code === 'TICKER_NOT_FOUND_OPTIONAL') {
            const error: any = new Error(result?.error || 'Ticker not found in our database.');
            error.code = 'TICKER_NOT_FOUND_OPTIONAL';
            error.error_code = 'TICKER_NOT_FOUND_OPTIONAL'; // Also set error_code for compatibility
            throw error;
          }
          throw new Error(result?.error || `HTTP ${res.status}`);
        }

        // Extract ticker fetch flags from response
        const needsTickerFetch = result.needs_ticker_fetch === true;
        const newTicker = result.new_ticker || null;

        // Optimistic update: Add/update transaction in cache immediately for instant UI feedback
        // This happens AFTER API succeeds, so we know the save worked
        queryClient.setQueryData<ManualTransactionFormData[]>(['transactions', accountId], (old) => {
          if (!old) return old;
          if (isEdit) {
            // Update existing transaction
            return old.map((t) => (t.id === data.id ? { ...data, ...result.transaction } : t));
          } else {
            // Add new transaction
            return [...old, { ...data, ...result.transaction }];
          }
        });

        // Invalidate transactions query for truth sync (refetches from DB to confirm)
        // This ensures the cache matches the database state
        if (user?.id) {
          queryClient.invalidateQueries({ queryKey: ['transactions', accountId] });
          
          // Immediately check rebuild status to start polling if rebuild is in progress
          // This ensures the toast appears right away
          queryClient.refetchQueries({ queryKey: ['private-cache-rebuild-status', user.id] });
          
          // Optional: Show toast to indicate background work is happening
          // Dashboard will update naturally when background recalculation completes
          toast.success(isEdit ? 'Transaction updated. Updating performance...' : 'Transaction added. Updating performance...', { duration: 2000 });
        }

        // Clear snapshot only after a successful save
        originalRef.current = { ticker: null, type: null, txId: null };
        return { success: true, needsTickerFetch, newTicker };
      } catch (err: any) {
        console.error('[handleManualTransactionSubmit] API save failed:', err);

        // Check if this is a TICKER_NOT_FOUND_OPTIONAL error (for non-ticker support)
        if (err.code === 'TICKER_NOT_FOUND_OPTIONAL' || err.error_code === 'TICKER_NOT_FOUND_OPTIONAL' || err.message?.includes('TICKER_NOT_FOUND_OPTIONAL')) {
          // Don't show toast - let the form handle it with dialog
          // Re-throw the error so the form can catch it and show the dialog
          throw err;
        }

        // Try to parse the response body to check for error_code
        let errorMessage = 'Failed to save transaction';
        let errorCode = '';

        try {
          // The error might contain JSON response data with our error_code
          if (err.message && err.message.includes('TICKER_NOT_FOUND')) {
            errorCode = 'TICKER_NOT_FOUND';
            errorMessage =
              'This ticker symbol does not exist in our database. Please check the symbol and try again.';
          } else if (err.response) {
            // Try to extract error from response if available
            const responseData = err.response.data;
            if (responseData && responseData.error_code === 'TICKER_NOT_FOUND') {
              errorCode = 'TICKER_NOT_FOUND';
              errorMessage =
                'This ticker symbol does not exist in our database. Please check the symbol and try again.';
            } else if (responseData && responseData.error) {
              errorMessage = responseData.error;
            }
          } else if (err.message && !err.message.includes('Failed to fetch')) {
            // Only use the error message if it's not the generic "Failed to fetch"
            errorMessage = err.message;
          }
        } catch (parseError) {
          console.warn('Could not parse error response:', parseError);
        }

        toast.error(errorMessage);

        // Throw the error with the message and code so it can be caught and displayed in the form
        const enhancedError = new Error(errorMessage);
        (enhancedError as any).code = errorCode;
        throw enhancedError;
      }
    },
    [editingTransaction, queryClient, user?.id]
  );

  // Edit a specific transaction
  const handleEditTransactionClick = useCallback((transaction: ManualTransactionFormData) => {
    // Capture snapshot synchronously to avoid races
    originalRef.current = {
      ticker: transaction.ticker ?? null,
      type: transaction.type ?? null,
      txId: transaction.id ?? null,
    };

    setEditingTransaction(transaction);
  }, []);

  // Delete a transaction
  const handleDeleteTransaction = useCallback(
    async (transactionId: string, accountId: string) => {
      if (!accountId) {
        console.error('Cannot delete, accountId not set.');
        return;
      }
      // No confirmation here - component handles it

      // Store old data for potential rollback (before API call)
      const oldTransactions = queryClient.getQueryData<ManualTransactionFormData[]>(['transactions', accountId]);

      // Delete via backend API
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('No auth token');

        const resp = await fetch(`${BASE_URL}/transactions/delete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ account_id: accountId, transaction_id: transactionId }),
        });

        if (!resp.ok) {
          throw new Error((await resp.json().catch(() => ({})))?.error || `HTTP ${resp.status}`);
        }

        await resp.json();

        // Optimistic update: Remove transaction from cache immediately for instant UI feedback
        // This happens AFTER API succeeds, so we know the deletion worked
        queryClient.setQueryData<ManualTransactionFormData[]>(['transactions', accountId], (old) => {
          if (!old) return old;
          return old.filter((t) => t.id !== transactionId);
        });

        // Invalidate transactions query for truth sync (refetches from DB to confirm)
        // This ensures the cache matches the database state
        if (user?.id) {
          queryClient.invalidateQueries({ queryKey: ['transactions', accountId] });
          
          // Immediately check rebuild status to start polling if rebuild is in progress
          // This ensures the toast appears right away
          queryClient.refetchQueries({ queryKey: ['private-cache-rebuild-status', user.id] });
          
          // Optional: Show toast to indicate background work is happening
          // Dashboard will update naturally when background recalculation completes
          toast.success('Transaction deleted. Updating performance...', { duration: 2000 });
        }
      } catch (error) {
        // Revert optimistic update on error (if we had updated it)
        // Note: Since we update after API succeeds, this is mainly for safety
        if (oldTransactions) {
          queryClient.setQueryData<ManualTransactionFormData[]>(['transactions', accountId], oldTransactions);
        }
        console.error('Error deleting transaction via API:', error);
        toast.error('Failed to delete transaction.');
        throw error;
      }
    },
    [queryClient, user?.id]
  );

  // Clear editing transaction
  const clearEditingTransaction = useCallback(() => {
    setEditingTransaction(undefined);
    originalRef.current = { ticker: null, type: null, txId: null };
  }, []);

  return {
    // State
    editingTransaction,

    // Actions
    handleManualTransactionSubmit,
    handleEditTransactionClick,
    handleDeleteTransaction,
    clearEditingTransaction,
    setEditingTransaction,
  };
};
